import type { CSSProperties } from 'react';
import cloudSvg from '../../components/atomic/atmosphere/clouds/cloud.svg';
import './FailDeliveryClouds.css';

type CloudConfig = {
  x: number;
  y: number;
  scale: number;
  /** Figma fill opacity (cloud.svg already has fill-opacity 0.5). */
  fillOpacity: number;
  duration?: string;
};

/** cloud.svg intrinsic size; small clouds in Figma are 222.734375×154.47. */
const CLOUD_BASE_WIDTH = 509;
const SMALL_SCALE = 222.734375 / CLOUD_BASE_WIDTH;
/** Matches fill-opacity baked into cloud.svg. */
const SVG_FILL_OPACITY = 0.5;

/**
 * Positions, scales and fill opacities from Figma clouds (150:17646)
 * on game_over_screen.
 */
const FAIL_DELIVERY_CLOUDS: readonly CloudConfig[] = [
  { x: 691, y: 64.5, scale: 1, fillOpacity: 0.5, duration: '90s' },
  { x: -102, y: 1626.5, scale: 1, fillOpacity: 0.5, duration: '105s' },
  { x: 730, y: 953.5, scale: 1, fillOpacity: 0.5, duration: '80s' },
  { x: -161, y: 339.5, scale: 1, fillOpacity: 0.5, duration: '98s' },
  { x: 93.5, y: 494.07421875, scale: 1, fillOpacity: 0.1, duration: '88s' },
  {
    x: 486.8671875,
    y: 1072.68359375,
    scale: 1,
    fillOpacity: 0.1,
    duration: '95s',
  },
  { x: -285, y: 1389.75, scale: 1, fillOpacity: 0.1, duration: '110s' },
  {
    x: 825.626953125,
    y: 1651.29296875,
    scale: SMALL_SCALE,
    fillOpacity: 0.5,
    duration: '72s',
  },
  {
    x: 75.6875,
    y: 851.69140625,
    scale: SMALL_SCALE,
    fillOpacity: 0.5,
    duration: '78s',
  },
  {
    x: 152.5,
    y: 25.83984375,
    scale: SMALL_SCALE,
    fillOpacity: 0.1,
    duration: '82s',
  },
  {
    x: 594.408203125,
    y: 376.60546875,
    scale: SMALL_SCALE,
    fillOpacity: 0.5,
    duration: '85s',
  },
];

export type FailDeliveryCloudsProps = {
  animation?: 'static' | 'drift';
};

/** Fail-delivery cloud layer — Figma game_over_screen / clouds (150:17646). */
export function FailDeliveryClouds({
  animation = 'drift',
}: FailDeliveryCloudsProps) {
  const drifting = animation === 'drift';

  return (
    <div
      className={['fail-delivery-clouds', drifting ? 'fail-delivery-clouds--drift' : null]
        .filter(Boolean)
        .join(' ')}
      aria-hidden
    >
      {FAIL_DELIVERY_CLOUDS.map((cloud, index) => (
        <img
          key={index}
          className="fail-delivery-clouds__item"
          src={cloudSvg}
          alt=""
          draggable={false}
          style={
            {
              left: cloud.x,
              top: cloud.y,
              opacity: cloud.fillOpacity / SVG_FILL_OPACITY,
              ...(drifting
                ? {
                    '--cloud-x': `${cloud.x}px`,
                    '--cloud-scale': cloud.scale,
                    '--cloud-duration': cloud.duration ?? '90s',
                  }
                : {
                    transform: `scale(${cloud.scale})`,
                  }),
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}
